/**
 * Evidence Breakdown Tab
 *
 * EPIC 62: Redesigned to show Inputs/Derived/Result grouping with source badges
 *
 * Shown to LENDER+ disclosure levels.
 * Displays: Inputs (with source badges), Derived (with formulas), Result (final score)
 */

import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { EvidenceContract, ComponentItem } from '@/types/evidenceContract';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface EvidenceBreakdownTabProps {
  evidence: EvidenceContract | null;
}

// Color palette for charts
const COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#84CC16', // lime
];

export function EvidenceBreakdownTab({ evidence }: EvidenceBreakdownTabProps) {
  // WP-8 Slice 8D.1: history-input fallback contract — no algo_run_evidence
  // row, no components, no calculation. Render a bounded lender-safe
  // Inputs/Result view derived only from public fields on the synthesized
  // contract (label, period, summary.display_value, summary.one_liner).
  if (evidence?.display_source === 'history_input_fallback') {
    return <SyntheticHistoryFallbackView evidence={evidence} />;
  }

  if (!evidence?.components) {
    return (
      <div className="text-center text-muted-foreground py-8">
        No breakdown available for this metric
      </div>
    );
  }

  const { components, value, unit } = evidence;
  const { count: _count, breakdown_type, items } = components;

  // For METRIC breakdown type (factor details), show as a list instead of chart
  // BUG-UI-033: VERASCORE breakdown shows hierarchical factor view
  const isMetricBreakdown = breakdown_type === 'METRIC';
  const isVeraScoreBreakdown = breakdown_type === 'VERASCORE';

  // EPIC 62: Check if items have breakdown_group (INPUTS/DERIVED/RESULT)
  const hasBreakdownGroups = items.some(item => item.breakdown_group);

  // BUG-UI-060/063: Sort DTI breakdown items in logical calculation order
  const sortedItems = sortBreakdownItems(items, evidence.metric_key);

  // EPIC 62: Render Inputs/Derived/Result view if breakdown groups exist
  if (hasBreakdownGroups) {
    return <InputsDerivedResultView items={sortedItems} unit={unit} metricKey={evidence.metric_key} />;
  }

  // Prepare chart data
  const chartData = sortedItems.map((item, idx) => ({
    name: item.name,
    value: item.percentage || (item.value && value && !isMetricBreakdown ? (item.value / value) * 100 : 0),
    actualValue: item.value,
    count: item.count,
    fill: COLORS[idx % COLORS.length],
    group: item.group,
    formula: item.formula,
  }));

  // Check if any items have values or counts
  // Note: 0 is a valid value, so we need !== undefined, not truthiness check
  const hasValues = chartData.some(d => d.actualValue !== undefined);
  const hasCounts = items.some(i => i.count !== undefined);
  const hasGroups = items.some(i => i.group);
  const hasFormulas = items.some(i => i.formula);

  // BUG-UI-023: Group items by their group field if present
  const groupedItems = hasGroups
    ? chartData.reduce((acc, item) => {
        const groupName = item.group || 'Other';
        if (!acc[groupName]) acc[groupName] = [];
        acc[groupName].push(item);
        return acc;
      }, {} as Record<string, typeof chartData>)
    : null;

  // Debug: log computed values with details
  if (import.meta.env.DEV) console.log('[EvidenceBreakdownTab] chartData:', chartData, 'hasValues:', hasValues);
  if (import.meta.env.DEV) console.log('[EvidenceBreakdownTab] chartData actualValues:', chartData.map(d => ({ name: d.name, actualValue: d.actualValue, type: typeof d.actualValue })));

  // BUG-UI-030: Math Check section moved to CalculationTab (where it belongs)

  // BUG-UI-033: Render hierarchical VeraScore breakdown with factor contributions
  if (isVeraScoreBreakdown) {
    return <VeraScoreBreakdownView items={sortedItems} evidence={evidence} />;
  }

  // FIX: Render INPUTS/DERIVED/RESULT view for income metrics (gross_income_6m)
  const isIncomeMetric = evidence.metric_key?.includes('gross_income') ||
                         evidence.metric_key?.includes('income_6m') ||
                         evidence.metric_key?.includes('deposited_income');
  if (isIncomeMetric) {
    return <IncomeBreakdownView items={sortedItems} unit={unit} evidence={evidence} />;
  }

  // FIX Jan 22, 2026: Render INPUTS/DERIVED/RESULT view for Bottom 10 KPI metrics
  const metricKey = evidence.metric_key?.toLowerCase() || '';
  const isBottom10KPIMetric = [
    'money_in_1m',
    'money_out_1m',
    'cash_on_hand',
    'monthly_debt_payments',
    'total_debt',
    'money_leftover_1m',
    'net_savings_1m',
    'dti_ratio'
  ].includes(metricKey);

  if (isBottom10KPIMetric) {
    return <Bottom10BreakdownView items={sortedItems} unit={unit} evidence={evidence} />;
  }

  return (
    <div className="space-y-6">
      {/* Summary stats */}
      <div className="flex items-center gap-4 p-4 bg-slate-700 rounded-lg">
        <div className="text-center flex-1">
          <div className="text-2xl font-bold text-white">
            {items.length}
          </div>
          <div className="text-xs text-slate-400">
            {/* BUG-UI-033: Show appropriate label for breakdown type */}
            {isMetricBreakdown ? 'Components' : getBreakdownLabel(breakdown_type)}
          </div>
        </div>
      </div>

      {/* BUG-EPIC62-002: Legend for colored bullets in old breakdown view */}
      <div className="p-3 bg-slate-700/50 rounded-lg border border-slate-600">
        <div className="text-xs font-semibold text-slate-300 mb-2">Legend</div>
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[0] }} />
            <span className="text-slate-400">Component colors match chart sections</span>
          </div>
        </div>
      </div>

      {/* Pie Chart - Only show for CATEGORY breakdown, not METRIC or VERASCORE */}
      {!isMetricBreakdown && !isVeraScoreBreakdown && chartData.some(d => d.value > 0) && (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                label={({ name, value }) => `${name}: ${value.toFixed(0)}%`}
                labelLine={false}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string, props: any) => {
                  const item = props.payload;
                  if (item.actualValue !== undefined) {
                    return [formatValue(item.actualValue, unit), name];
                  }
                  return [`${value.toFixed(1)}%`, name];
                }}
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', color: '#fff' }}
                labelStyle={{ color: '#fff' }}
              />
              <Legend wrapperStyle={{ color: '#e2e8f0' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* BUG-UI-023: Detailed breakdown table with optional grouping */}
      <div className="border border-slate-600 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-700">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-white">
                {isMetricBreakdown ? 'Component' : 'Category'}
              </th>
              {!isMetricBreakdown && (
                <th className="px-4 py-2 text-right font-medium text-white">%</th>
              )}
              {hasValues && (
                <th className="px-4 py-2 text-right font-medium text-white">
                  Value
                </th>
              )}
              {hasCounts && (
                <th className="px-4 py-2 text-right font-medium text-white">Count</th>
              )}
              {hasFormulas && (
                <th className="px-4 py-2 text-left font-medium text-white">Formula</th>
              )}
            </tr>
          </thead>
          <tbody className="bg-slate-800">
            {/* BUG-UI-023: Render grouped items if groups exist */}
            {groupedItems ? (
              Object.entries(groupedItems).map(([groupName, groupItems]) => (
                <React.Fragment key={groupName}>
                  {/* Group header row */}
                  <tr className="bg-slate-700/50 border-t border-slate-600">
                    <td colSpan={4 + (hasFormulas ? 1 : 0)} className="px-4 py-2 font-semibold text-blue-400 text-xs uppercase tracking-wider">
                      {groupName}
                    </td>
                  </tr>
                  {/* Group items */}
                  {groupItems.map((item, idx) => (
                    <tr key={`${groupName}-${idx}`} className="border-t border-slate-700">
                      <td className="px-4 py-2 flex items-center gap-2 text-white pl-6">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.fill }}
                        />
                        {item.name}
                      </td>
                      {!isMetricBreakdown && (
                        <td className="px-4 py-2 text-right text-slate-300">
                          {item.value.toFixed(1)}%
                        </td>
                      )}
                      {hasValues && (
                        <td className="px-4 py-2 text-right font-mono text-slate-300">
                          {item.actualValue !== undefined ? formatMetricValue(item.name, item.actualValue) : '—'}
                        </td>
                      )}
                      {hasCounts && (
                        <td className="px-4 py-2 text-right text-slate-300">
                          {item.count !== undefined ? item.count : '—'}
                        </td>
                      )}
                      {hasFormulas && (
                        <td className="px-4 py-2 text-left text-slate-400 font-mono text-xs">
                          {item.formula || '—'}
                        </td>
                      )}
                    </tr>
                  ))}
                </React.Fragment>
              ))
            ) : (
              /* Flat list (no grouping) */
              chartData.map((item, idx) => (
                <tr key={idx} className="border-t border-slate-700">
                  <td className="px-4 py-2 flex items-center gap-2 text-white">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.fill }}
                    />
                    {item.name}
                  </td>
                  {!isMetricBreakdown && (
                    <td className="px-4 py-2 text-right text-slate-300">
                      {item.value.toFixed(1)}%
                    </td>
                  )}
                  {hasValues && (
                    <td className="px-4 py-2 text-right font-mono text-slate-300">
                      {item.actualValue !== undefined ? formatMetricValue(item.name, item.actualValue) : '—'}
                    </td>
                  )}
                  {hasCounts && (
                    <td className="px-4 py-2 text-right text-slate-300">
                      {item.count !== undefined ? item.count : '—'}
                    </td>
                  )}
                  {hasFormulas && (
                    <td className="px-4 py-2 text-left text-slate-400 font-mono text-xs">
                      {item.formula || '—'}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function getBreakdownLabel(type: string): string {
  switch (type) {
    case 'CATEGORY': return 'Transactions';
    case 'LIABILITY_TYPE': return 'Liabilities';
    case 'ACCOUNT': return 'Accounts';
    case 'FACTOR': return 'Factors';
    case 'METRIC': return 'Metrics';
    default: return 'Items';
  }
}

function formatValue(value: number, unit: string): string {
  if (unit === 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  }
  return value.toLocaleString();
}

/**
 * Smart metric value formatter
 *
 * Detects the appropriate format based on metric name patterns.
 * Fixes: BUG-UI-004, 009, 011, 015, 021, 022, 027
 */
function formatMetricValue(name: string, value: number): string {
  const lowerName = name.toLowerCase();

  // === SCORES (0-100 scale, no unit) ===
  // BUG-UI-021: Scores formatted as currency ("$37", "$100")
  if (lowerName.includes('score') ||
      (lowerName.includes('factor') && !lowerName.includes('count'))) {
    return Math.round(value).toString();
  }

  // === WEIGHTS (0-1 decimal, show as decimal not currency) ===
  // BUG-UI-004, BUG-UI-022: Weights formatted as currency ("$0.15")
  if (lowerName.includes('weight') || lowerName === 'contribution') {
    return value.toFixed(2);
  }

  // === COUNTS (integers, no decimals, no currency) ===
  // BUG-UI-015, BUG-UI-027: Payment counts showing as "$4", "$1"
  if (lowerName.includes('count') ||
      lowerName.includes('late payment') ||
      lowerName.includes('on time payment') ||
      lowerName.includes('on-time') ||
      lowerName.includes('txn') ||
      lowerName.includes('transaction')) {
    return Math.round(value).toLocaleString();
  }

  // === AGE IN MONTHS (show as "X months") ===
  // BUG-UI-015, BUG-UI-027: Age shows "$2" instead of "2 months"
  if (lowerName.includes('age') && lowerName.includes('month')) {
    return `${Math.round(value)} months`;
  }

  // === MONTHS OF COVERAGE/RESERVES ===
  // BUG-UI-028: Shows "204.1 mo" - keep this format but ensure it's not currency
  if (lowerName.includes('months') ||
      lowerName.includes('coverage') ||
      lowerName.includes('reserves') && !lowerName.includes('balance')) {
    return `${value.toFixed(1)} mo`;
  }

  // === PERCENTAGES ===
  // BUG-UI-011: Cash Flow % shows "$0" instead of "0%"
  if (lowerName.includes('ratio') ||
      lowerName.includes('rate') ||
      lowerName.includes('margin') ||
      lowerName.includes('%') ||
      lowerName.includes('pct') ||
      lowerName.includes('utilization')) {
    return `${value.toFixed(1)}%`;
  }

  // === CURRENCY VALUES (explicit patterns) ===
  // BUG-UI-009: Expenses show "512.5 mo" instead of "$512.5"
  // BUG-UI-046: "Checking Total" missing $ - added 'total' and 'checking' patterns
  if (lowerName.includes('income') ||
      lowerName.includes('payment') ||
      lowerName.includes('balance') ||
      lowerName.includes('savings') ||
      lowerName.includes('cash') ||
      lowerName.includes('debt') ||
      lowerName.includes('deposit') ||
      lowerName.includes('withdrawal') ||
      lowerName.includes('money') ||
      lowerName.includes('amount') ||
      lowerName.includes('expense') ||
      lowerName.includes('limit') ||
      lowerName.includes('gross') ||
      lowerName.includes('checking') ||  // BUG-UI-046: Checking Total
      lowerName.includes('total') ||     // BUG-UI-046: *Total fields are usually currency
      lowerName.includes('net') && !lowerName.includes('ratio')) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  // === DEFAULT: Plain number ===
  return value.toLocaleString();
}

// BUG-UI-030: formatStepValue moved to EvidenceCalculationTab

/**
 * BUG-UI-060/063/101: Sort breakdown items in logical calculation order
 *
 * For DTI factor, the calculation flow is:
 * 1. Yearly Gross Income (starting point)
 * 2. Monthly Gross Income (yearly / 12)
 * 3. Min Debt Payments (monthly debt obligations)
 * 4. DTI Ratio (debt / income = result)
 *
 * BUG-UI-101: For Income metrics (gross_income_6m), the flow is:
 * 1. Six Month Sum / Deposit Sum (raw input)
 * 2. Month Count (input)
 * 3. Avg Monthly (calculated)
 * 4. Total / Annualized (final output)
 *
 * This ensures the breakdown displays items in the order they're computed.
 */
function sortBreakdownItems(
  items: Array<{ name: string; value?: number; percentage?: number; count?: number; group?: string; formula?: string }>,
  metricKey?: string
): typeof items {
  if (!metricKey) return items;

  // BUG-UI-101: Income metrics sorting (inputs first, output last)
  const isIncomeMetric = metricKey.includes('gross_income') || metricKey.includes('income_6m') || metricKey.includes('deposited_income');

  // BUG-UI-065 FIX: Apply custom sorting for DTI-related metrics including dti_ratio
  const isDtiMetric = metricKey.includes('debt_to_income') || metricKey === 'dti_ratio';

  if (!isIncomeMetric && !isDtiMetric) {
    return items;
  }

  // BUG-UI-101: Income metric order (inputs first, calculations, then output)
  const incomeOrder: Record<string, number> = {
    'six month sum': 1,
    'six_month_sum': 1,
    'deposit sum': 1,
    'sum of deposits': 1,
    'month count': 2,
    'month_count': 2,
    'months': 2,
    'avg monthly': 3,
    'average monthly': 3,
    'monthly average': 3,
    'total': 4,
    'annualized': 4,
    'annual': 4,
    'yearly': 4,
  };

  // Define the logical order for DTI breakdown components
  const dtiOrder: Record<string, number> = {
    'yearly gross income': 1,
    'yearly_gross_income': 1,
    'annual income': 1,
    'monthly gross income': 2,
    'monthly_gross_income': 2,
    'monthly income': 2,
    'min debt payments': 3,
    'min_debt_payments': 3,
    'minimum debt payments': 3,
    'monthly debt payments': 3,
    'debt payments': 3,
    'dti ratio': 4,
    'dti_ratio': 4,
    'debt to income ratio': 4,
    'debt-to-income ratio': 4,
  };

  const orderMap = isIncomeMetric ? incomeOrder : dtiOrder;

  return [...items].sort((a, b) => {
    const aKey = a.name.toLowerCase();
    const bKey = b.name.toLowerCase();

    // Find matching order keys
    const aOrder = Object.entries(orderMap).find(([key]) => aKey.includes(key))?.[1] ?? 99;
    const bOrder = Object.entries(orderMap).find(([key]) => bKey.includes(key))?.[1] ?? 99;

    return aOrder - bOrder;
  });
}

/**
 * BUG-UI-033: Specialized VeraScore breakdown showing hierarchical factor contributions
 * BUG-UI-081/082/083/084/085: ENHANCED TABLE per feedback design
 * Shows: Factor | Raw Score | Weight | Weighted Score | Contribution | Reason Code
 */
import { SimpleTooltip } from '@/components/ui/Tooltip';

interface BreakdownItem {
  name: string;
  value?: number;
  percentage?: number;
  count?: number;
  group?: string;
  formula?: string;
  weight?: number; // BUG-UI-081: Explicit weight field
  reason_code?: string; // BUG-UI-085: Reason code for coaching
}

// BUG-UI-084: Factor descriptions for tooltips
const FACTOR_DESCRIPTIONS: Record<string, {
  description: string;
  scoreRanges: { min: number; max: number; label: string; color: string }[];
  tips: string;
}> = {
  'Cash Flow': {
    description: 'Measures monthly income vs expenses ratio. A higher score indicates better cash management.',
    scoreRanges: [
      { min: 0, max: 40, label: 'Poor', color: 'text-red-400' },
      { min: 41, max: 60, label: 'Fair', color: 'text-amber-400' },
      { min: 61, max: 80, label: 'Good', color: 'text-green-400' },
      { min: 81, max: 100, label: 'Excellent', color: 'text-emerald-400' },
    ],
    tips: 'Increase income or reduce discretionary expenses to improve this score.',
  },
  'Savings': {
    description: 'Monthly savings rate as percentage of income. Measures ability to build financial reserves.',
    scoreRanges: [
      { min: 0, max: 40, label: 'Low (<5%)', color: 'text-red-400' },
      { min: 41, max: 60, label: 'Moderate (5-10%)', color: 'text-amber-400' },
      { min: 61, max: 80, label: 'Good (10-20%)', color: 'text-green-400' },
      { min: 81, max: 100, label: 'Excellent (>20%)', color: 'text-emerald-400' },
    ],
    tips: 'Set up automatic transfers to savings accounts on payday.',
  },
  'Liquid Reserves': {
    description: 'Months of expenses covered by liquid assets. Measures emergency fund adequacy.',
    scoreRanges: [
      { min: 0, max: 40, label: '<3 months', color: 'text-red-400' },
      { min: 41, max: 60, label: '3-6 months', color: 'text-amber-400' },
      { min: 61, max: 80, label: '6-9 months', color: 'text-green-400' },
      { min: 81, max: 100, label: '>9 months', color: 'text-emerald-400' },
    ],
    tips: 'Build emergency fund covering 6+ months of expenses.',
  },
  'Debt-to-Income': {
    description: 'Monthly debt payments as percentage of gross income. Lower DTI = higher score.',
    scoreRanges: [
      { min: 0, max: 40, label: 'High (>36%)', color: 'text-red-400' },
      { min: 41, max: 60, label: 'Moderate (28-36%)', color: 'text-amber-400' },
      { min: 61, max: 80, label: 'Good (20-28%)', color: 'text-green-400' },
      { min: 81, max: 100, label: 'Excellent (<20%)', color: 'text-emerald-400' },
    ],
    tips: 'Pay down high-interest debt first. Avoid new debt.',
  },
  'Debt Management': {
    description: 'Credit utilization and debt reduction patterns over time.',
    scoreRanges: [
      { min: 0, max: 40, label: 'Poor', color: 'text-red-400' },
      { min: 41, max: 60, label: 'Fair', color: 'text-amber-400' },
      { min: 61, max: 80, label: 'Good', color: 'text-green-400' },
      { min: 81, max: 100, label: 'Excellent', color: 'text-emerald-400' },
    ],
    tips: 'Keep credit utilization below 30%. Pay more than minimums.',
  },
  'Payment History': {
    description: 'Track record of on-time payments. Critical factor for creditworthiness.',
    scoreRanges: [
      { min: 0, max: 40, label: 'Many late', color: 'text-red-400' },
      { min: 41, max: 60, label: 'Some late', color: 'text-amber-400' },
      { min: 61, max: 80, label: 'Rarely late', color: 'text-green-400' },
      { min: 81, max: 100, label: 'Always on time', color: 'text-emerald-400' },
    ],
    tips: 'Set up autopay for all recurring bills to never miss a payment.',
  },
  'Other Composite': {
    description: 'Stage 1 weighted combination: DTI (20%) + Debt Mgmt (17.5%) + Payment History (10%).',
    scoreRanges: [
      { min: 0, max: 40, label: 'Below Average', color: 'text-red-400' },
      { min: 41, max: 60, label: 'Average', color: 'text-amber-400' },
      { min: 61, max: 80, label: 'Above Average', color: 'text-green-400' },
      { min: 81, max: 100, label: 'Excellent', color: 'text-emerald-400' },
    ],
    tips: 'Improve individual Stage 1 factors to raise this composite score.',
  },
};

// BUG-UI-085: Reason codes with descriptive TEXT per feedback design (NO icons, just text)
function getReasonCode(factorName: string, score: number): { text: string; type: 'good' | 'warning' | 'bad' | 'drill' } {
  // Detailed reason texts per factor and score range
  const reasonTexts: Record<string, { good: string; warning: string; bad: string }> = {
    'Cash Flow': {
      good: 'Healthy income vs expenses',
      warning: 'Expenses trending higher',
      bad: 'Inflow below expense target',
    },
    'Savings': {
      good: 'Strong savings rate',
      warning: 'Savings could improve',
      bad: 'No recurring savings activity',
    },
    'Liquid Reserves': {
      good: 'Good reserve buffer',
      warning: 'Build more reserves',
      bad: 'Emergency fund low',
    },
    'Debt-to-Income': {
      good: 'Low debt burden',
      warning: 'DTI above target',
      bad: 'High debt-to-income ratio',
    },
    'Debt Management': {
      good: 'Debt well managed',
      warning: 'Credit utilization rising',
      bad: 'Debt management issues',
    },
    'Payment History': {
      good: 'Excellent payment record',
      warning: 'Some late payments',
      bad: 'Multiple late payments',
    },
  };

  if (factorName.includes('Other Composite') || factorName.includes('Composite')) {
    return { text: 'Blend: DTI + Debt Mgmt + Payment Hist.', type: 'drill' };
  }

  const reasons = reasonTexts[factorName] || { good: 'On track', warning: 'Room to improve', bad: 'Needs attention' };

  if (score >= 70) {
    return { text: reasons.good, type: 'good' };
  }
  if (score >= 40) {
    return { text: reasons.warning, type: 'warning' };
  }
  return { text: reasons.bad, type: 'bad' };
}

// BUG-UI-087: Get color class based on score
function getScoreColorClass(score: number): string {
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-green-400';
  if (score >= 40) return 'text-amber-400';
  return 'text-red-400';
}

function VeraScoreBreakdownView({ items, evidence }: { items: BreakdownItem[]; evidence?: EvidenceContract | null }) {
  // Feature flag: detect if curve scoring was used based on backend evidence data
  const scoringSystem = (evidence?.intermediate_metrics?.scoring_system as string) || 'linear';
  const isCurveScoring = scoringSystem === 'curve';
  // BUG-UI-089: State for JSON toggle
  const [showJSON, setShowJSON] = React.useState(false);

  // Separate final score from factor items
  const finalScoreItem = items.find(i => i.group === 'Result' || i.name.toLowerCase().includes('final'));
  const factorItems = items.filter(i => i.group !== 'Result' && !i.name.toLowerCase().includes('final'));

  // BUG-UI-095/054 FIX: Separate Stage 2 (direct) factors from Stage 1 (composite) factors
  // Stage 2 factors: Cash Flow, Savings, Liquid Reserves - contribute directly
  // Stage 1 factors: DTI, Debt Mgmt, Payment History - feed into Other Composite
  // BUG-UI-054 FIX: Filter by GROUP field (set in buildComponents) instead of NAME
  // This ensures we display all factors returned by backend, regardless of exact name match
  const stage2Factors = factorItems.filter(f =>
    f.group === 'Stage 2 Factors' ||
    // Fallback to name matching for backwards compatibility
    ['Cash Flow', 'Savings', 'Liquid Reserves'].includes(f.name)
  );
  const stage1Factors = factorItems.filter(f =>
    f.group === 'Stage 1 Factors (Other Composite)' ||
    // Fallback to name matching for backwards compatibility
    ['Debt-to-Income', 'Debt Management', 'Payment History'].includes(f.name)
  );
  const compositeFactors = factorItems.filter(f =>
    f.group === 'Core Factors' || // Group name for Other Composite
    f.name.toLowerCase().includes('composite') || f.name.toLowerCase().includes('stage 1')
  );

  // BUG-UI-082 FIX: Calculate POINTS contributed (Score × Weight%)
  // Note: `weight` is stored as decimal (0.15), `percentage` is stored as percent (15)
  // Example: Cash Flow score 37 × 15% weight = 5.55 pts
  const calculatePoints = (score: number, weightDecimal: number) => {
    return score * weightDecimal; // weight is 0.15, not 15, so no division needed
  };

  // Calculate total points for the final score
  const totalPoints = factorItems.reduce((sum, item) => {
    // Use weight (decimal like 0.15) or convert percentage (like 15) back to decimal
    const weightDecimal = item.weight ?? (item.percentage ? item.percentage / 100 : 0);
    const score = item.value ?? 0;
    return sum + calculatePoints(score, weightDecimal);
  }, 0);

  // BUG-UI-095: Calculate stage subtotals
  const stage2Total = stage2Factors.reduce((sum, item) => {
    const weightDecimal = item.weight ?? (item.percentage ? item.percentage / 100 : 0);
    return sum + calculatePoints(item.value ?? 0, weightDecimal);
  }, 0);
  const stage2WeightTotal = stage2Factors.reduce((sum, item) => {
    return sum + (item.percentage ?? (item.weight ? item.weight * 100 : 0));
  }, 0);

  // Build export data
  const exportData = factorItems.map(f => {
    const weightDecimal = f.weight ?? (f.percentage ? f.percentage / 100 : 0);
    return {
      factor: f.name,
      raw_score: f.value,
      weight_decimal: weightDecimal,
      weight_percent: f.percentage ?? (f.weight ? f.weight * 100 : 0),
      weighted_score_pts: calculatePoints(f.value ?? 0, weightDecimal),
      reason: getReasonCode(f.name, f.value ?? 0).text,
      group: f.group,
    };
  });

  // BUG-UI-088: Column header tooltip component
  const HeaderTooltip = ({ children, tooltip }: { children: React.ReactNode; tooltip: string }) => (
    <SimpleTooltip content={<span className="text-xs">{tooltip}</span>} side="top">
      <span className="cursor-help inline-flex items-center gap-1">
        {children}
        <span className="text-slate-500 text-[10px]">?</span>
      </span>
    </SimpleTooltip>
  );

  // BUG-UI-084: Factor tooltip content
  const FactorTooltipContent = ({ factorName, score }: { factorName: string; score: number }) => {
    const info = FACTOR_DESCRIPTIONS[factorName] || FACTOR_DESCRIPTIONS[factorName.replace(' (Stage 1)', '')];
    if (!info) return <span>{factorName}</span>;

    const range = info.scoreRanges.find(r => score >= r.min && score <= r.max);
    return (
      <div className="space-y-2 max-w-xs">
        <div className="font-semibold text-white">{factorName}</div>
        <div className="text-slate-300 text-xs">{info.description}</div>
        {range && (
          <div className="text-xs">
            <span className="text-slate-400">Your score: </span>
            <span className={range.color}>{score} ({range.label})</span>
          </div>
        )}
        <div className="text-xs text-blue-300 border-t border-slate-600 pt-2">
          💡 {info.tips}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* ============================================================ */}
      {/* SECTION 1: INPUTS - The 6 factor scores used as inputs       */}
      {/* ============================================================ */}
      <div className="border border-slate-600 rounded-lg overflow-hidden">
        <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
            Inputs
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            6 factor scores used to calculate VeraScore
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-slate-300">Factor</th>
              <th className="px-4 py-2 text-right font-medium text-slate-300">Score</th>
              <th className="px-4 py-2 text-left font-medium text-slate-300">Source</th>
            </tr>
          </thead>
          <tbody className="bg-slate-800">
            {[...stage2Factors, ...stage1Factors].map((factor, idx) => {
              const score = factor.value ?? 0;
              const scoreColor = getScoreColorClass(score);
              const factorKey = factor.name.toLowerCase().replace(/\s+/g, '_').replace('(stage_1)', '').replace('(stage_2)', '').trim() + '_factor';
              return (
                <tr key={`input-${idx}`} className="border-t border-slate-700">
                  <td className="px-4 py-3 text-white">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-400">●</span>
                      {factor.name}
                    </div>
                  </td>
                  <td className={`px-4 py-3 text-right font-mono ${scoreColor}`}>
                    {Math.round(score)}/100
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-slate-700 text-slate-300 border border-slate-600">
                      {factorKey}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ============================================================ */}
      {/* SECTION 2: DERIVED - Weighted calculation of factors         */}
      {/* ============================================================ */}
      <div className="border border-slate-600 rounded-lg overflow-hidden">
        <div className="bg-slate-700 px-4 py-3 border-b border-slate-600 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              Derived
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Weighted calculation: Score × Weight = Points contributed
            </p>
          </div>
          {/* BUG-UI-089: Toggle Show/Hide JSON button */}
          <button
            className="text-xs text-slate-400 hover:text-white border border-slate-600 px-3 py-1.5 rounded flex items-center gap-1 transition-colors"
            onClick={() => setShowJSON(!showJSON)}
          >
            {showJSON ? '📄 Hide JSON' : '📥 Export JSON'}
          </button>
        </div>
        <div className="bg-slate-900">
        <table className="w-full text-sm">
          <thead className="bg-slate-800 border-b border-slate-700">
            <tr>
              {/* BUG-UI-088: Column headers with glossary tooltips */}
              <th className="px-4 py-3 text-left font-medium text-slate-300">
                <HeaderTooltip tooltip="Financial health factor being measured">Factor</HeaderTooltip>
              </th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">
                <HeaderTooltip tooltip="Raw score from 0-100 based on member's financial data">Raw Score</HeaderTooltip>
              </th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">
                <HeaderTooltip tooltip="Factor's importance weight (4 decimal precision for audit)">Weight</HeaderTooltip>
              </th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">
                <HeaderTooltip tooltip="Score × Weight = points contributed to final score">Weighted Score</HeaderTooltip>
              </th>
              <th className="px-4 py-3 text-right font-medium text-slate-300">
                <HeaderTooltip tooltip="This factor's weight as % of total (should sum to ~100%)">Contribution</HeaderTooltip>
              </th>
              <th className="px-4 py-3 text-left font-medium text-slate-300">
                <HeaderTooltip tooltip="Status indicator with coaching suggestions">Reason Code</HeaderTooltip>
              </th>
            </tr>
          </thead>
          <tbody>
            {/* BUG-UI-095: Stage 2 Header - Direct Contributing Factors */}
            <tr className="bg-blue-900/30 border-t border-blue-600">
              <td colSpan={6} className="px-4 py-2">
                <span className="text-blue-300 text-xs font-semibold uppercase tracking-wide">
                  ▸ Stage 2: Direct Contributing Factors
                </span>
              </td>
            </tr>
            {/* Stage 2 Factors */}
            {stage2Factors.map((factor, idx) => {
              const score = factor.value ?? 0;
              const weightDecimal = factor.weight ?? (factor.percentage ? factor.percentage / 100 : 0);
              const weightPct = factor.percentage ?? (factor.weight ? factor.weight * 100 : 0);
              const points = calculatePoints(score, weightDecimal);
              const reasonCode = getReasonCode(factor.name, score);
              const scoreColor = getScoreColorClass(score);
              return (
                <tr key={`s2-${idx}`} className="border-t border-slate-800 hover:bg-slate-800/50 transition-colors">
                  <td className="px-4 py-3"><SimpleTooltip content={<FactorTooltipContent factorName={factor.name} score={score} />} side="right"><span className="text-white font-medium cursor-help hover:text-blue-300">{factor.name}</span></SimpleTooltip></td>
                  <td className={`px-4 py-3 text-right font-mono ${scoreColor}`}>{Math.round(score)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-400">{weightPct.toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right"><SimpleTooltip content={<span className="font-mono text-xs">{Math.round(score)} × {weightPct.toFixed(0)}% = {points.toFixed(1)} pts</span>} side="top"><span className="font-mono text-green-400 cursor-help"><span className="bg-slate-700 px-2 py-0.5 rounded">{points.toFixed(1)} pts</span></span></SimpleTooltip></td>
                  <td className="px-4 py-3 text-right font-mono text-slate-400">{weightPct.toFixed(1)}%</td>
                  <td className="px-4 py-3"><span className={`inline-block px-2 py-1 rounded text-xs font-medium ${reasonCode.type === 'good' ? 'bg-green-900/40 text-green-400 border border-green-800/50' : ''} ${reasonCode.type === 'warning' ? 'bg-amber-900/40 text-amber-400 border border-amber-800/50' : ''} ${reasonCode.type === 'bad' ? 'bg-red-900/40 text-red-400 border border-red-800/50' : ''}`}>{reasonCode.text}</span></td>
                </tr>
              );
            })}
            {/* Stage 2 Subtotal */}
            <tr className="bg-blue-900/20 border-t border-blue-800">
              <td className="px-4 py-2 text-blue-300 text-sm font-medium">Stage 2 Subtotal</td>
              <td className="px-4 py-2 text-right font-mono text-slate-400">—</td>
              <td className="px-4 py-2 text-right font-mono text-blue-300 text-sm">{stage2WeightTotal.toFixed(1)}%</td>
              <td className="px-4 py-2 text-right font-mono text-blue-300 text-sm">{stage2Total.toFixed(1)} pts</td>
              <td className="px-4 py-2"></td>
              <td className="px-4 py-2"></td>
            </tr>

            {/* BUG-UI-095: Stage 1 Header - Composite Factor (contains DTI, Debt Mgmt, Payment History) */}
            <tr className="bg-purple-900/30 border-t-2 border-purple-600">
              <td colSpan={6} className="px-4 py-2">
                <span className="text-purple-300 text-xs font-semibold uppercase tracking-wide">
                  ▸ Stage 1: Composite Factor (includes DTI, Debt Mgmt, Payment History)
                </span>
              </td>
            </tr>
            {/* Stage 1 Component Factors - shown for transparency */}
            {stage1Factors.map((factor, idx) => {
              const score = factor.value ?? 0;
              const weightDecimal = factor.weight ?? (factor.percentage ? factor.percentage / 100 : 0);
              const weightPct = factor.percentage ?? (factor.weight ? factor.weight * 100 : 0);
              const points = calculatePoints(score, weightDecimal);
              const reasonCode = getReasonCode(factor.name, score);
              const scoreColor = getScoreColorClass(score);
              return (
                <tr key={`s1-${idx}`} className="border-t border-slate-800 hover:bg-slate-800/50 transition-colors bg-purple-900/10">
                  <td className="px-4 py-3 pl-8"><SimpleTooltip content={<FactorTooltipContent factorName={factor.name} score={score} />} side="right"><span className="text-slate-300 font-medium cursor-help hover:text-purple-300 text-sm">↳ {factor.name}</span></SimpleTooltip></td>
                  <td className={`px-4 py-3 text-right font-mono text-sm ${scoreColor}`}>{Math.round(score)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-500 text-sm">{weightPct.toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right"><span className="font-mono text-purple-400 text-sm"><span className="bg-slate-700/50 px-2 py-0.5 rounded">{points.toFixed(1)} pts</span></span></td>
                  <td className="px-4 py-3 text-right font-mono text-slate-500 text-sm">{weightPct.toFixed(1)}%</td>
                  <td className="px-4 py-3"><span className={`inline-block px-2 py-1 rounded text-xs font-medium ${reasonCode.type === 'good' ? 'bg-green-900/40 text-green-400 border border-green-800/50' : ''} ${reasonCode.type === 'warning' ? 'bg-amber-900/40 text-amber-400 border border-amber-800/50' : ''} ${reasonCode.type === 'bad' ? 'bg-red-900/40 text-red-400 border border-red-800/50' : ''}`}>{reasonCode.text}</span></td>
                </tr>
              );
            })}
            {/* Other Composite Row */}
            {compositeFactors.map((factor, idx) => {
              const score = factor.value ?? 0;
              const weightDecimal = factor.weight ?? (factor.percentage ? factor.percentage / 100 : 0);
              const weightPct = factor.percentage ?? (factor.weight ? factor.weight * 100 : 0);
              const points = calculatePoints(score, weightDecimal);
              const reasonCode = getReasonCode(factor.name, score);
              const scoreColor = getScoreColorClass(score);
              return (
                <tr key={`comp-${idx}`} className="border-t border-purple-800 bg-purple-900/20">
                  <td className="px-4 py-3"><SimpleTooltip content="Blended score from DTI, Debt Management, and Payment History factors" side="right"><span className="text-purple-300 font-semibold cursor-help">→ {factor.name}</span></SimpleTooltip></td>
                  <td className={`px-4 py-3 text-right font-mono ${scoreColor}`}>{Math.round(score)}</td>
                  <td className="px-4 py-3 text-right font-mono text-purple-300">{weightPct.toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right"><span className="font-mono text-purple-400 font-semibold"><span className="bg-purple-900/50 px-2 py-0.5 rounded border border-purple-700">{points.toFixed(1)} pts</span></span></td>
                  <td className="px-4 py-3 text-right font-mono text-purple-300">{weightPct.toFixed(1)}%</td>
                  <td className="px-4 py-3"><span className="inline-block px-2 py-1 rounded text-xs font-medium bg-purple-900/40 text-purple-300 border border-purple-700">{reasonCode.text}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* BUG-UI-089: Show JSON toggle - inside DERIVED section */}
        {showJSON && (
          <div className="p-4 border-t border-slate-700">
            <div className="text-xs text-slate-400 mb-2">JSON Export Data:</div>
            <pre className="bg-slate-800 rounded p-3 overflow-auto text-xs text-slate-300 max-h-64">
              {JSON.stringify({ factors: exportData, final_score: finalScoreItem?.value || Math.round(totalPoints), generated_at: new Date().toISOString() }, null, 2)}
            </pre>
          </div>
        )}

        {/* Scoring model explanation inside DERIVED section - adapts to curve vs linear */}
        <div className="text-xs text-slate-500 px-4 py-3 border-t border-slate-700">
          {isCurveScoring ? (
            <SimpleTooltip
              content={
                <div className="space-y-2 text-xs max-w-sm">
                  <div className="font-semibold text-white border-b border-slate-600 pb-1">2-Stage Scoring Model</div>
                  <div><span className="text-blue-300">Stage 2:</span> Direct factors (Cash Flow, Savings, Liquid Reserves) = {stage2Total.toFixed(1)} pts</div>
                  <div><span className="text-purple-300">Stage 1:</span> Composite factor blends DTI + Debt Mgmt + Payment History</div>
                  <div className="text-slate-400 pt-1 border-t border-slate-600">
                    Final score applies curve normalization for fair distribution across all applicants.
                  </div>
                </div>
              }
              side="top"
            >
              <span className="cursor-help inline-flex items-center gap-1">
                ℹ️ 2-Stage Model: Stage 2 ({stage2Total.toFixed(1)} pts) + Stage 1 Composite → Weighted Sum: {totalPoints.toFixed(1)}
              </span>
            </SimpleTooltip>
          ) : (
            <span className="inline-flex items-center gap-1">
              ℹ️ Weighted Sum: Σ contributions = {totalPoints.toFixed(1)}
            </span>
          )}
        </div>
        </div>
      </div>

      {/* ============================================================ */}
      {/* SECTION 3: RESULT - Final VeraScore                          */}
      {/* ============================================================ */}
      <div className="border border-slate-600 rounded-lg overflow-hidden">
        <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
            Result
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            Final calculated VeraScore
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-slate-300">Metric</th>
              <th className="px-4 py-2 text-right font-medium text-slate-300">Value</th>
              <th className="px-4 py-2 text-left font-medium text-slate-300">Formula</th>
            </tr>
          </thead>
          <tbody className="bg-slate-800">
            <tr className="border-t border-slate-700">
              <td className="px-4 py-3 text-white">
                <div className="flex items-center gap-2">
                  <span className="text-green-400">◉</span>
                  VeraScore
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <span className="font-mono text-green-400 font-bold bg-slate-700 px-3 py-1 rounded text-xl">
                  {Math.round(finalScoreItem?.value || totalPoints)}/100
                </span>
              </td>
              <td className="px-4 py-3 text-slate-400 text-xs font-mono">
                {isCurveScoring ? 'two_stage_curve(6_factors)' : 'weighted_sum(6_factors)'}
              </td>
            </tr>
          </tbody>
        </table>
        {isCurveScoring && (
          <div className="px-4 py-3 bg-slate-800/50 border-t border-slate-700 text-xs text-slate-500">
            Note: Final score may differ from weighted sum ({totalPoints.toFixed(1)}) due to curve normalization.
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// FIX: INCOME BREAKDOWN VIEW (gross_income_6m) - INPUTS/DERIVED/RESULT structure
// =============================================================================

interface IncomeBreakdownViewProps {
  items: ComponentItem[];
  unit: string;
  evidence: EvidenceContract;
}

function IncomeBreakdownView({ items, unit: _unit, evidence: _evidence }: IncomeBreakdownViewProps) {
  // Extract values from items by name matching
  const getItemValue = (patterns: string[]): number | undefined => {
    const item = items.find(i =>
      patterns.some(p => i.name.toLowerCase().includes(p.toLowerCase()))
    );
    return item?.value;
  };

  // Map items to INPUTS/DERIVED/RESULT categories
  const sixMonthSum = getItemValue(['six month sum', 'six_month_sum', 'deposit sum']);
  const monthCount = getItemValue(['month count', 'month_count', 'months']);
  const avgMonthly = getItemValue(['avg monthly', 'average monthly', 'monthly average']);
  const total = getItemValue(['total', 'annualized', 'annual']);

  // Format currency values
  const formatCurrency = (val: number | undefined): string => {
    if (val === undefined) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
  };

  const formatNumber = (val: number | undefined): string => {
    if (val === undefined) return '—';
    return val.toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* INPUTS Section */}
      <div className="border border-slate-600 rounded-lg overflow-hidden">
        <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
            Inputs
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            Raw deposit data from linked bank accounts
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-slate-300">Component</th>
              <th className="px-4 py-2 text-right font-medium text-slate-300">Value</th>
              <th className="px-4 py-2 text-left font-medium text-slate-300">Source</th>
            </tr>
          </thead>
          <tbody className="bg-slate-800">
            <tr className="border-t border-slate-700">
              <td className="px-4 py-3 text-white">
                <div className="flex items-center gap-2">
                  <span className="text-red-400">●</span>
                  Six Month Sum
                </div>
              </td>
              <td className="px-4 py-3 text-right font-mono text-slate-300">
                {formatCurrency(sixMonthSum)}
              </td>
              <td className="px-4 py-3">
                <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">
                  Plaid Deposits
                </span>
              </td>
            </tr>
            <tr className="border-t border-slate-700">
              <td className="px-4 py-3 text-white">
                <div className="flex items-center gap-2">
                  <span className="text-amber-400">●</span>
                  Month Count
                </div>
              </td>
              <td className="px-4 py-3 text-right font-mono text-slate-300">
                {formatNumber(monthCount)}
              </td>
              <td className="px-4 py-3">
                <span className="px-2 py-0.5 text-xs rounded-full bg-slate-500/20 text-slate-300 border border-slate-500/30">
                  Analysis Period
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* DERIVED Section */}
      <div className="border border-slate-600 rounded-lg overflow-hidden">
        <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
            Derived
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            Calculated from inputs
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-slate-300">Component</th>
              <th className="px-4 py-2 text-right font-medium text-slate-300">Value</th>
              <th className="px-4 py-2 text-left font-medium text-slate-300">Formula</th>
            </tr>
          </thead>
          <tbody className="bg-slate-800">
            <tr className="border-t border-slate-700">
              <td className="px-4 py-3 text-white">
                <div className="flex items-center gap-2">
                  <span className="text-green-400">●</span>
                  Avg Monthly
                </div>
              </td>
              <td className="px-4 py-3 text-right font-mono text-slate-300">
                {formatCurrency(avgMonthly)}
              </td>
              <td className="px-4 py-3">
                <code className="text-xs text-slate-400 bg-slate-700 px-2 py-1 rounded">
                  six_month_sum ÷ month_count
                </code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* RESULT Section */}
      <div className="border border-emerald-600/50 rounded-lg overflow-hidden">
        <div className="bg-emerald-900/30 px-4 py-3 border-b border-emerald-600/50">
          <h3 className="text-sm font-semibold text-emerald-300 uppercase tracking-wider">
            Result
          </h3>
          <p className="text-xs text-emerald-400/70 mt-1">
            Final annualized income value
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-emerald-900/20">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-emerald-300">Component</th>
              <th className="px-4 py-2 text-right font-medium text-emerald-300">Value</th>
              <th className="px-4 py-2 text-left font-medium text-emerald-300">Formula</th>
            </tr>
          </thead>
          <tbody className="bg-slate-800">
            <tr className="border-t border-emerald-600/30">
              <td className="px-4 py-3 text-white">
                <div className="flex items-center gap-2">
                  <span className="text-blue-400">●</span>
                  <span className="font-semibold">Total (Annualized)</span>
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <span className="font-mono text-lg font-bold text-emerald-400">
                  {formatCurrency(total)}
                </span>
              </td>
              <td className="px-4 py-3">
                <code className="text-xs text-slate-400 bg-slate-700 px-2 py-1 rounded">
                  avg_monthly × 12
                </code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// FIX Jan 22, 2026: BOTTOM 10 KPI BREAKDOWN VIEW (INPUTS/DERIVED/RESULT structure)
// Handles: money_in_1m, money_out_1m, cash_on_hand, monthly_debt_payments,
//          total_debt, money_leftover_1m, net_savings_1m, dti_ratio
// =============================================================================

interface Bottom10BreakdownViewProps {
  items: ComponentItem[];
  unit: string;
  evidence: EvidenceContract;
}

function Bottom10BreakdownView({ items, unit: _unit, evidence }: Bottom10BreakdownViewProps) {
  const metricKey = evidence.metric_key?.toLowerCase() || '';

  // Format currency values
  const formatCurrency = (val: number | undefined): string => {
    if (val === undefined) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
  };

  const formatPercent = (val: number | undefined): string => {
    if (val === undefined) return '—';
    return `${val.toFixed(1)}%`;
  };

  // Helper to find item by name patterns
  const getItemValue = (patterns: string[]): number | undefined => {
    const item = items.find(i =>
      patterns.some(p => i.name.toLowerCase().includes(p.toLowerCase()))
    );
    return item?.value;
  };

  // Get metric-specific configuration
  const getMetricConfig = () => {
    switch (metricKey) {
      case 'money_in_1m':
        return {
          title: 'Money In (Monthly)',
          inputs: [
            { name: 'Checking Deposits', patterns: ['checking deposit'], source: 'Plaid Deposits', color: 'text-blue-400' },
            { name: 'Savings Deposits', patterns: ['saving deposit', 'savings deposit'], source: 'Plaid Deposits', color: 'text-green-400' },
            { name: 'Investment Deposits', patterns: ['investment deposit'], source: 'Plaid Deposits', color: 'text-purple-400' },
          ],
          result: { name: 'Total Money In', patterns: ['total', 'sum deposit', 'money in'], formula: 'checking + savings + investment' },
          format: formatCurrency,
        };
      case 'money_out_1m':
        return {
          title: 'Money Out (Monthly)',
          inputs: [
            { name: 'Checking Withdrawals', patterns: ['checking withdrawal'], source: 'Plaid Transactions', color: 'text-blue-400' },
            { name: 'Savings Withdrawals', patterns: ['saving withdrawal', 'savings withdrawal'], source: 'Plaid Transactions', color: 'text-green-400' },
            { name: 'Investment Withdrawals', patterns: ['investment withdrawal'], source: 'Plaid Transactions', color: 'text-purple-400' },
          ],
          result: { name: 'Total Money Out', patterns: ['total', 'sum withdrawal', 'money out'], formula: 'checking + savings + investment' },
          format: formatCurrency,
        };
      case 'cash_on_hand':
        return {
          title: 'Cash on Hand',
          inputs: [
            { name: 'Checking Balance', patterns: ['checking balance', 'checking total', 'checking'], source: 'Plaid Balances', color: 'text-blue-400' },
            { name: 'Savings Balance', patterns: ['saving balance', 'savings balance', 'savings total', 'savings'], source: 'Plaid Balances', color: 'text-green-400' },
          ],
          result: { name: 'Total Cash on Hand', patterns: ['total', 'cash on hand'], formula: 'checking + savings' },
          format: formatCurrency,
        };
      case 'monthly_debt_payments':
        return {
          title: 'Minimum Debt Payments',
          inputs: [
            { name: 'Loan Payments', patterns: ['loan payment', 'total loan'], source: 'Plaid Liabilities', color: 'text-red-400' },
            { name: 'Credit Card Payments', patterns: ['credit payment', 'credit card', 'total credit'], source: 'Plaid Liabilities', color: 'text-orange-400' },
          ],
          result: { name: 'Total Minimum Payments', patterns: ['total', 'actual debt', 'minimum payment', 'debt payment'], formula: 'loan_payments + credit_payments' },
          format: formatCurrency,
        };
      case 'total_debt':
        return {
          title: 'Outstanding Debt',
          inputs: [
            { name: 'Short-term Debt', patterns: ['short term', 'short_term', 'credit card', 'credit balance'], source: 'Plaid Liabilities', color: 'text-orange-400' },
            { name: 'Long-term Debt', patterns: ['loan balance', 'long term', 'long_term', 'mortgage', 'auto loan', 'student loan'], source: 'Plaid Liabilities', color: 'text-red-400' },
          ],
          result: { name: 'Total Outstanding Debt', patterns: ['total debt', 'outstanding'], formula: 'short_term + long_term' },
          format: formatCurrency,
        };
      case 'money_leftover_1m':
        return {
          title: 'Money Leftover (Monthly)',
          inputs: [
            { name: 'Money In', patterns: ['money in', 'income', 'deposit'], source: 'Derived', color: 'text-green-400' },
            { name: 'Money Out', patterns: ['money out', 'expense', 'withdrawal'], source: 'Derived', color: 'text-red-400' },
          ],
          result: { name: 'Money Leftover', patterns: ['leftover', 'net flow', 'net cash'], formula: 'money_in - money_out' },
          format: formatCurrency,
        };
      // BUG-UI-128 FIX: Include Investment/Retirement accounts in Net Savings breakdown
      // SR 11-7 requires full input transparency - all components used in calculation must be displayed
      case 'net_savings_1m':
        return {
          title: 'Net Savings (Monthly)',
          inputs: [
            // Savings account component
            { name: 'Savings Deposits', patterns: ['saving_deposits', 'saving deposit', 'savings deposit', 'savings_net'], source: 'Plaid Deposits', color: 'text-green-400' },
            { name: 'Savings Withdrawals', patterns: ['saving_withdrawals', 'saving withdrawal', 'savings withdrawal'], source: 'Plaid Transactions', color: 'text-red-400' },
            // Retirement account component (401k, IRA, etc.)
            { name: 'Retirement Deposits', patterns: ['retirement_deposits', 'retirement deposit', 'retirement deposits', 'retirement_net'], source: 'Plaid Deposits', color: 'text-purple-400' },
            { name: 'Retirement Withdrawals', patterns: ['retirement_withdrawals', 'retirement withdrawal', 'retirement withdrawals'], source: 'Plaid Transactions', color: 'text-orange-400' },
            // Investment account component
            { name: 'Investment Deposits', patterns: ['investment_deposits', 'investment deposit', 'investment deposits', 'investment_net'], source: 'Plaid Deposits', color: 'text-blue-400' },
            { name: 'Investment Withdrawals', patterns: ['investment_withdrawals', 'investment withdrawal', 'investment withdrawals'], source: 'Plaid Transactions', color: 'text-amber-400' },
          ],
          result: { name: 'Net Savings', patterns: ['net_savings', 'net saving', 'total saving', 'savings'], formula: '(savings deposits - withdrawals) + (retirement deposits - withdrawals) + (investment deposits - withdrawals)' },
          format: formatCurrency,
        };
      // BUG-UI-127 FIX: DTI inputs are currency ($), result is percentage (%)
      case 'dti_ratio':
        return {
          title: 'Debt-to-Income Ratio',
          inputs: [
            { name: 'Monthly Debt Payments', patterns: ['debt payment', 'minimum payment', 'monthly debt', 'min_debt_payments'], source: 'Plaid Liabilities', color: 'text-red-400' },
            { name: 'Monthly Income', patterns: ['income', 'gross income', 'monthly income', 'money in', 'monthly_gross_income'], source: 'Plaid Deposits', color: 'text-green-400' },
          ],
          result: { name: 'DTI Ratio', patterns: ['dti', 'ratio', 'dti_ratio'], formula: '(debt_payments / income) × 100' },
          format: formatPercent,
          inputFormat: formatCurrency, // BUG-UI-127: Inputs are currency, result is percentage
        };
      default:
        return null;
    }
  };

  const config = getMetricConfig();
  if (!config) {
    // Fallback to showing raw items if no config
    return (
      <div className="space-y-4">
        <div className="text-xs text-slate-400">Raw breakdown for {metricKey}</div>
        {items.map((item, idx) => (
          <div key={idx} className="flex justify-between p-2 bg-slate-700 rounded">
            <span className="text-white">{item.name}</span>
            <span className="text-slate-300 font-mono">{formatCurrency(item.value)}</span>
          </div>
        ))}
      </div>
    );
  }

  // Extract values for each input
  const inputValues = config.inputs.map(input => ({
    ...input,
    value: getItemValue(input.patterns),
  }));

  const resultValue = getItemValue(config.result.patterns) ||
    (evidence as any).computed_result ||
    (evidence as any).value;

  return (
    <div className="space-y-6">
      {/* INPUTS Section */}
      <div className="border border-slate-600 rounded-lg overflow-hidden">
        <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
            Inputs
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            Raw data from linked accounts
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-slate-300">Component</th>
              <th className="px-4 py-2 text-right font-medium text-slate-300">Value</th>
              <th className="px-4 py-2 text-left font-medium text-slate-300">Source</th>
            </tr>
          </thead>
          <tbody className="bg-slate-800">
            {inputValues.map((input, idx) => (
              <tr key={idx} className="border-t border-slate-700">
                <td className="px-4 py-3 text-white">
                  <div className="flex items-center gap-2">
                    <span className={input.color}>●</span>
                    {input.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-mono text-slate-300">
                  {/* BUG-UI-127 FIX: Use inputFormat for inputs if available, otherwise use format */}
                  {(config.inputFormat || config.format)(input.value)}
                </td>
                <td className="px-4 py-3">
                  <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">
                    {input.source}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* RESULT Section */}
      <div className="border border-emerald-600/50 rounded-lg overflow-hidden">
        <div className="bg-emerald-900/30 px-4 py-3 border-b border-emerald-600/50">
          <h3 className="text-sm font-semibold text-emerald-300 uppercase tracking-wider">
            Result
          </h3>
          <p className="text-xs text-emerald-400/70 mt-1">
            Computed metric value
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-emerald-900/20">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-emerald-300">Metric</th>
              <th className="px-4 py-2 text-right font-medium text-emerald-300">Value</th>
              <th className="px-4 py-2 text-left font-medium text-emerald-300">Formula</th>
            </tr>
          </thead>
          <tbody className="bg-slate-800">
            <tr className="border-t border-emerald-600/30">
              <td className="px-4 py-3 text-white">
                <div className="flex items-center gap-2">
                  <span className="text-emerald-400">●</span>
                  <span className="font-semibold">{config.result.name}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <span className="font-mono text-lg font-bold text-emerald-400">
                  {config.format(resultValue)}
                </span>
              </td>
              <td className="px-4 py-3">
                <code className="text-xs text-slate-400 bg-slate-700 px-2 py-1 rounded">
                  {config.result.formula}
                </code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// EPIC 62 US62.1: INPUTS/DERIVED/RESULT VIEW
// =============================================================================

interface InputsDerivedResultViewProps {
  items: ComponentItem[];
  unit: string;
  metricKey: string;
}

function InputsDerivedResultView({ items, unit, metricKey: _metricKey }: InputsDerivedResultViewProps) {
  const grouped = groupByBreakdownType(items);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  const toggleExpand = (itemName: string) => {
    setExpandedItem(expandedItem === itemName ? null : itemName);
  };

  return (
    <div className="space-y-6">
      {/* INPUTS Section */}
      {grouped.inputs.length > 0 && (
        <div className="border border-slate-600 rounded-lg overflow-hidden">
          <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              Inputs
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Raw data used in this calculation
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-slate-300">Component</th>
                <th className="px-4 py-2 text-right font-medium text-slate-300">Value</th>
                <th className="px-4 py-2 text-left font-medium text-slate-300">Source</th>
              </tr>
            </thead>
            <tbody className="bg-slate-800">
              {grouped.inputs.map((item, idx) => (
                <React.Fragment key={idx}>
                  <tr
                    className={`border-t border-slate-700 ${item.drilldown_data ? 'cursor-pointer hover:bg-slate-700/50' : ''}`}
                    onClick={() => item.drilldown_data && toggleExpand(item.name)}
                  >
                    <td className="px-4 py-3 text-white">
                      <div className="flex items-center gap-2">
                        {item.drilldown_data && (
                          expandedItem === item.name ?
                            <ChevronDown className="w-4 h-4 text-slate-400" /> :
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                        )}
                        <span className="text-blue-400">●</span>
                        {item.name}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-300">
                      {item.value !== undefined ? formatBreakdownValue(item.value, unit, item.name) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {item.source_badge && <SourceBadge source={item.source_badge} />}
                    </td>
                  </tr>
                  {/* Drilldown panel */}
                  {item.drilldown_data && expandedItem === item.name && (
                    <tr className="border-t border-slate-700">
                      <td colSpan={3} className="px-4 py-4 bg-slate-900/50">
                        <DrilldownPanel data={item.drilldown_data} unit={unit} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* DERIVED Section */}
      {grouped.derived.length > 0 && (
        <div className="border border-slate-600 rounded-lg overflow-hidden">
          <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              Derived
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Calculated from inputs
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-slate-300">Component</th>
                <th className="px-4 py-2 text-right font-medium text-slate-300">Value</th>
                <th className="px-4 py-2 text-left font-medium text-slate-300">Formula</th>
              </tr>
            </thead>
            <tbody className="bg-slate-800">
              {grouped.derived.map((item, idx) => (
                <tr key={idx} className="border-t border-slate-700">
                  <td className="px-4 py-3 text-white">
                    <div className="flex items-center gap-2">
                      <span className="text-amber-400">○</span>
                      {item.name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-300">
                    {item.value !== undefined ? formatBreakdownValue(item.value, unit, item.name) : '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-400 font-mono text-xs">
                    {item.formula || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* RESULT Section */}
      {grouped.result.length > 0 && (
        <div className="border border-slate-600 rounded-lg overflow-hidden">
          <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              Result
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Final calculated value
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-slate-300">Component</th>
                <th className="px-4 py-2 text-right font-medium text-slate-300">Value</th>
                <th className="px-4 py-2 text-left font-medium text-slate-300">Mapping</th>
              </tr>
            </thead>
            <tbody className="bg-slate-800">
              {grouped.result.map((item, idx) => (
                <tr key={idx} className="border-t border-slate-700">
                  <td className="px-4 py-3 text-white">
                    <div className="flex items-center gap-2">
                      <span className="text-green-400">◉</span>
                      {item.name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-green-400 font-semibold">
                    {item.value !== undefined ? formatBreakdownValue(item.value, unit, item.name) : '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">
                    {item.formula || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// EPIC 62 US62.2: DRILLDOWN PANEL COMPONENT
// =============================================================================

interface DrilldownPanelProps {
  data: import('@/types/evidenceContract').ComponentDrilldown;
  unit: string;
}

function DrilldownPanel({ data, unit }: DrilldownPanelProps) {
  const { type, included_items, excluded_items, selection_rationale, comparison_data } = data;

  return (
    <div className="space-y-4">
      {/* Comparison data (for income) */}
      {comparison_data && (
        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <div className="text-xs font-semibold text-slate-300 mb-3">Income Basis Comparison</div>
          <div className="space-y-2">
            {comparison_data.attested !== undefined && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-400">Attested (Self-Reported)</span>
                <span className={`font-mono ${comparison_data.selected === 'ATTESTED' ? 'text-green-400 font-semibold' : 'text-slate-300'}`}>
                  {formatBreakdownValue(comparison_data.attested, unit, 'income')}
                  {comparison_data.selected === 'ATTESTED' && <span className="ml-2 text-green-500">✓ Selected</span>}
                </span>
              </div>
            )}
            {comparison_data.deposited !== undefined && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-400">Deposited (6-month avg)</span>
                <span className={`font-mono ${comparison_data.selected === 'DEPOSITED' ? 'text-green-400 font-semibold' : 'text-slate-300'}`}>
                  {formatBreakdownValue(comparison_data.deposited, unit, 'income')}
                  {comparison_data.selected === 'DEPOSITED' && <span className="ml-2 text-green-500">✓ Selected</span>}
                </span>
              </div>
            )}
            {comparison_data.verified !== undefined && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-400">Verified (Gross)</span>
                <span className={`font-mono ${comparison_data.selected === 'VERIFIED' ? 'text-green-400 font-semibold' : 'text-slate-300'}`}>
                  {formatBreakdownValue(comparison_data.verified, unit, 'income')}
                  {comparison_data.selected === 'VERIFIED' && <span className="ml-2 text-green-500">✓ Selected</span>}
                </span>
              </div>
            )}
          </div>
          {comparison_data.reason && (
            <div className="mt-3 pt-3 border-t border-slate-700">
              <div className="text-xs text-slate-400">
                <span className="font-semibold">Selection Reason:</span> {comparison_data.reason}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Included items */}
      {included_items && included_items.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-green-400 mb-2">
            INCLUDED ({included_items.length} {type === 'DEBT_PAYMENTS' ? 'obligations' : 'items'})
          </div>
          <div className="space-y-2">
            {included_items.map((item, idx) => (
              <div key={idx} className="bg-slate-800 rounded p-3 border border-slate-700">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-white">{item.name}</span>
                  <span className="text-sm font-mono text-slate-300">
                    {formatBreakdownValue(item.amount, unit, item.name)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <SourceBadge source={item.source} />
                  {item.confidence && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      item.confidence === 'HIGH' ? 'bg-green-500/20 text-green-300' :
                      item.confidence === 'MEDIUM' ? 'bg-amber-500/20 text-amber-300' :
                      'bg-red-500/20 text-red-300'
                    }`}>
                      {item.confidence}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Excluded items */}
      {excluded_items && excluded_items.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-red-400 mb-2">
            EXCLUDED ({excluded_items.length} items)
          </div>
          <div className="space-y-2">
            {excluded_items.map((item, idx) => (
              <div key={idx} className="bg-slate-800/50 rounded p-3 border border-slate-700/50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-slate-400 line-through">{item.name}</span>
                  <span className="text-sm font-mono text-slate-500">
                    {formatBreakdownValue(item.amount, unit, item.name)}
                  </span>
                </div>
                {item.reason && (
                  <div className="text-xs text-slate-500">
                    Reason: {item.reason}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selection rationale */}
      {selection_rationale && !comparison_data && (
        <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
          <div className="text-xs text-slate-400">
            <span className="font-semibold">Note:</span> {selection_rationale}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// EPIC 62: SOURCE BADGE COMPONENT
// BUG-UI-103/108 FIX: Updated labels to be user-friendly and consistent with Summary tab
// =============================================================================

interface SourceBadgeProps {
  source: 'ATTESTED' | 'DEPOSITED' | 'PLAID' | 'CRS' | 'HYBRID' | 'VERIFIED';
}

function SourceBadge({ source }: SourceBadgeProps) {
  // BUG-UI-103/108 FIX: Use user-friendly labels that match Summary tab terminology
  const badgeConfig = {
    ATTESTED: { label: 'Self-Reported', color: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
    DEPOSITED: { label: 'Bank Verified', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
    VERIFIED: { label: 'Verified', color: 'bg-green-500/20 text-green-300 border-green-500/30' },
    PLAID: { label: 'Bank Data', color: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' },
    CRS: { label: 'Credit Bureau', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
    HYBRID: { label: 'Multiple Sources', color: 'bg-slate-500/20 text-slate-300 border-slate-500/30' },
  };

  const config = badgeConfig[source];

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${config.color}`}>
      {config.label}
    </span>
  );
}

// =============================================================================
// EPIC 62: HELPER FUNCTIONS
// =============================================================================

/**
 * Group breakdown items by INPUTS/DERIVED/RESULT
 */
function groupByBreakdownType(items: ComponentItem[]): {
  inputs: ComponentItem[];
  derived: ComponentItem[];
  result: ComponentItem[];
} {
  return {
    inputs: items.filter(item => item.breakdown_group === 'INPUTS'),
    derived: items.filter(item => item.breakdown_group === 'DERIVED'),
    result: items.filter(item => item.breakdown_group === 'RESULT'),
  };
}

/**
 * Format breakdown values based on unit and component name
 * BUG-UI-107 FIX: Check counts BEFORE currency to avoid "Late Payments" being formatted as $1
 */
function formatBreakdownValue(value: number, unit: string, name: string): string {
  const lowerName = name.toLowerCase();

  // Scores (0-100 scale, no unit)
  if (lowerName.includes('score') || lowerName.includes('factor')) {
    return Math.round(value).toString();
  }

  // BUG-UI-107 FIX: Counts - check BEFORE currency patterns
  // "Late Payments", "On-Time Payments", "Number of X" are counts, not currency
  if (unit === 'COUNT' ||
      lowerName.includes('count') ||
      lowerName.includes('late payment') ||
      lowerName.includes('on-time payment') ||
      lowerName.includes('on time payment') ||
      lowerName.includes('number of')) {
    return Math.round(value).toLocaleString();
  }

  // BUG-UI-107 FIX: Age in months - check BEFORE generic months
  // "Age of Late Payments" should show "2 months" not "$2"
  if (lowerName.includes('age') && lowerName.includes('month')) {
    return `${Math.round(value)} months`;
  }

  // Percentages - BUG-UI-102 FIX: Also match '%' and 'pct' in name
  if (unit === 'PERCENT' || lowerName.includes('ratio') || lowerName.includes('rate') ||
      lowerName.includes('%') || lowerName.includes('pct')) {
    return `${value.toFixed(1)}%`;
  }

  // Currency - now excludes "late payment" patterns (handled above)
  if (unit === 'USD' || lowerName.includes('income') || lowerName.includes('debt') || lowerName.includes('expense') ||
      (lowerName.includes('payment') && !lowerName.includes('late') && !lowerName.includes('on-time') && !lowerName.includes('on time'))) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  // Months
  if (unit === 'MONTHS' || lowerName.includes('month')) {
    return `${value.toFixed(1)} months`;
  }

  // Default
  return value.toLocaleString();
}


// =============================================================================
// WP-8 Slice 8D.1: Lender-safe Inputs/Result view for the history-input
// fallback contract emitted by synthesizeEvidenceFromHistoryInput. Renders
// only public fields (label, period, summary.display_value, summary.one_liner)
// and never references run_id, algorithm_version, weights, thresholds, or
// any other internal-only taxonomy.
// =============================================================================
function SyntheticHistoryFallbackView({ evidence }: { evidence: EvidenceContract }) {
  const periodLabel = formatPeriodMonth(evidence.period?.start) ?? '—';
  const displayValue = evidence.summary?.display_value ?? '—';

  return (
    <div className="space-y-6">
      <div className="border border-slate-600 rounded-lg overflow-hidden">
        <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
            Inputs
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            Value reported from member history for this month
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-slate-300">Component</th>
              <th className="px-4 py-2 text-right font-medium text-slate-300">Value</th>
              <th className="px-4 py-2 text-left font-medium text-slate-300">Source</th>
            </tr>
          </thead>
          <tbody className="bg-slate-800">
            <tr className="border-t border-slate-700">
              <td className="px-4 py-3 text-white">{evidence.label}</td>
              <td className="px-4 py-3 text-right font-mono text-slate-200">{displayValue}</td>
              <td className="px-4 py-3">
                <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">
                  Member history
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="border border-slate-600 rounded-lg overflow-hidden">
        <div className="bg-slate-700 px-4 py-3 border-b border-slate-600">
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
            Result
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            Shown to the lender for {periodLabel}
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-slate-300">Metric</th>
              <th className="px-4 py-2 text-right font-medium text-slate-300">Value</th>
              <th className="px-4 py-2 text-left font-medium text-slate-300">Period</th>
            </tr>
          </thead>
          <tbody className="bg-slate-800">
            <tr className="border-t border-slate-700">
              <td className="px-4 py-3 text-white">{evidence.label}</td>
              <td className="px-4 py-3 text-right">
                <span className="font-mono text-green-400 bg-slate-700 px-3 py-1 rounded">
                  {displayValue}
                </span>
              </td>
              <td className="px-4 py-3 text-slate-300">{periodLabel}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {evidence.summary?.one_liner && (
        <p className="text-xs text-slate-400 italic">
          {evidence.summary.one_liner}
        </p>
      )}
    </div>
  );
}

function formatPeriodMonth(isoDate?: string): string | null {
  if (!isoDate) return null;
  // Parse YYYY-MM[-DD] in local time so we don't slip into the previous
  // month when the host TZ is west of UTC (matches synthesizer behavior).
  const m = /^(\d{4})-(\d{2})/.exec(isoDate);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  if (!year || !month) return null;
  const d = new Date(year, month - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}
